import React, { useContext, useEffect, useState } from 'react';
import { DefaultContext } from '../../../context/context_default';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import CostLegend from './CostLegend';
import FortuneChart from './FortuneChart';

// Importar ícones das fortunas
import imgIgn from '../../../assets/imagens/ign.png';
import imgVerbus from '../../../assets/imagens/verbus.png';
import imgKarma from '../../../assets/imagens/karma.png';
import imgDnama from '../../../assets/imagens/dnama.png';
import imgGnos from '../../../assets/imagens/gnos.png';
import imgOmna from '../../../assets/imagens/omna.png';

const DeckStats = ({ deck, arvore = [], matriz = [] }) => {
	const { listCards, raridades, tipos, subTipos, fortunas } = useContext(DefaultContext);
	const [activeTab, setActiveTab] = useState('arvore');
	const [stats, setStats] = useState({
		curvaMana: [],
		distribuicaoTipos: [],
		distribuicaoRaridades: [],
		distribuicaoMana: [],
		custoMedio: { arvore: 0, matriz: 0, total: 0 },
		totalCartas: { arvore: 0, matriz: 0, total: 0 }
	});

	// Mapeamento dos ícones das fortunas
	const fortunaIcons = {
		'Ign': imgIgn,
		'Verbus': imgVerbus,
		'Karma': imgKarma,
		'Dnama': imgDnama,
		'Gnos': imgGnos,
		'Omna': imgOmna
	};

	const COLORS = {
		raridades: {
			'comum': '#9CA3AF',
			'incomum': '#10B981',
			'rara': '#3B82F6',
			'épica': '#8B5CF6',
			'lendária': '#F59E0B'
		},
		tipos: {
			'criatura': '#EF4444',
			'feitiço': '#3B82F6',
			'artefato': '#6B7280',
			'encantamento': '#8B5CF6',
			'terreno': '#10B981'
		}
	};

	useEffect(() => {
		if (!listCards || !deck) return;

		// Usar apenas a árvore para todos os cálculos
		const cartasCompletas = arvore.map(cartaDeck => {
			const cartaCompleta = listCards.find(card => card.id === cartaDeck.id);
			return { ...cartaCompleta, qtd: cartaDeck.qtd || 1 };
		}).filter(Boolean);

		// Curva de Mana - APENAS DECK DA ÁRVORE
		const arvoreCompleta = arvore.map(cartaDeck => {
			const cartaCompleta = listCards.find(card => card.id === cartaDeck.id);
			return { ...cartaCompleta, qtd: cartaDeck.qtd || 1 };
		}).filter(Boolean);

		const curvaMana = Array(11).fill(0).map((_, i) => ({ custo: i === 10 ? '10+' : i.toString(), qtd: 0 }));
		arvoreCompleta.forEach(carta => {
			if (carta.custo !== undefined) {
				const custo = Math.min(parseInt(carta.custo) || 0, 10);
				curvaMana[custo].qtd += carta.qtd;
			}
		});

		// Distribuição por Tipos - usando context para buscar nomes (apenas árvore)
		const tiposCount = {};
		cartasCompletas.forEach(carta => {
			if (carta.tipo && Array.isArray(carta.tipo)) {
				carta.tipo.forEach(tipoId => {
					const tipoObj = tipos?.find(t => t.id === tipoId);
					const nomeExibicao = tipoObj?.tipo || `Tipo ${tipoId}`;
					tiposCount[nomeExibicao] = (tiposCount[nomeExibicao] || 0) + carta.qtd;
				});
			}
		});
		const distribuicaoTipos = Object.entries(tiposCount).map(([nome, qtd]) => ({ nome, qtd }));

		// Distribuição por Raridades - usando context para buscar nomes (apenas árvore)
		const raridadesCount = {};
		cartasCompletas.forEach(carta => {
			if (carta.raridade) {
				const raridadeObj = raridades?.find(r => r.id === carta.raridade);
				const nomeExibicao = raridadeObj?.raridade || `Raridade ${carta.raridade}`;
				raridadesCount[nomeExibicao] = (raridadesCount[nomeExibicao] || 0) + carta.qtd;
			}
		});
		const distribuicaoRaridades = Object.entries(raridadesCount).map(([nome, qtd]) => ({ nome, qtd }));

		// Distribuição de Tipos de Mana - usando fortunaCusto (apenas árvore)
		const manaCount = {};
		cartasCompletas.forEach(carta => {
			if (carta.fortunaCusto && Array.isArray(carta.fortunaCusto)) {
				carta.fortunaCusto.forEach(fortunaId => {
					const fortunaObj = fortunas?.find(f => f.id === fortunaId);
					const nomeExibicao = fortunaObj?.tipo || `Fortuna ${fortunaId}`;
					manaCount[nomeExibicao] = (manaCount[nomeExibicao] || 0) + carta.qtd;
				});
			}
		});
		const distribuicaoMana = Object.entries(manaCount).map(([nome, qtd]) => ({ nome, qtd })).sort((a, b) => b.qtd - a.qtd);

		// Custos médios
		const calcularCustoMedio = (cartas) => {
			const totalCusto = cartas.reduce((acc, carta) => {
				const custo = parseInt(carta.custo) || 0;
				return acc + (custo * carta.qtd);
			}, 0);
			const totalQtd = cartas.reduce((acc, carta) => acc + carta.qtd, 0);
			return totalQtd > 0 ? (totalCusto / totalQtd).toFixed(2) : '0.00';
		};

		const matrizCompleta = matriz.map(c => listCards.find(card => card.id === c.id)).filter(Boolean).map((c, i) => ({ ...c, qtd: matriz[i].qtd || 1 }));

		const custoMedio = {
			arvore: calcularCustoMedio(arvoreCompleta),
			matriz: calcularCustoMedio(matrizCompleta),
			total: calcularCustoMedio(cartasCompletas)
		};

		// Total de cartas (apenas árvore)
		const totalCartas = {
			arvore: arvore.reduce((acc, c) => acc + (c.qtd || 1), 0),
			matriz: matriz.reduce((acc, c) => acc + (c.qtd || 1), 0),
			total: arvore.reduce((acc, c) => acc + (c.qtd || 1), 0) + matriz.reduce((acc, c) => acc + (c.qtd || 1), 0)
		};

		setStats({
			curvaMana: curvaMana.filter(c => c.qtd > 0),
			distribuicaoTipos,
			distribuicaoRaridades,
			distribuicaoMana,
			custoMedio,
			totalCartas
		});
	}, [deck, arvore, matriz, listCards, raridades, tipos, subTipos, fortunas]);

	const CustomTooltip = ({ active, payload, label }) => {
		if (active && payload && payload.length) {
			const data = payload[0].payload;
			return (
				<div className="bg-slate-800 border border-slate-600 rounded p-2 shadow-lg">
					<p className="text-slate-200">{`${data.nome || label}: ${payload[0].value}`}</p>
				</div>
			);
		}
		return null;
	};

	return (
		<div className="space-y-6">
			{/* Resumo Geral */}
			<div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
				<h3 className="text-lg font-semibold text-slate-200 mb-4">📊 Resumo do Deck</h3>
				<div className="grid grid-cols-2 gap-4 text-sm">
					<div>
						<span className="text-slate-400">Total de Cartas:</span>
						<span className="text-slate-200 font-semibold ml-2">{stats.totalCartas.total}</span>
					</div>
					<div>
						<span className="text-slate-400">Custo Médio:</span>
						<span className="text-slate-200 font-semibold ml-2">{stats.custoMedio.total}</span>
					</div>
					<div>
						<span className="text-slate-400">Árvore:</span>
						<span className="text-slate-200 font-semibold ml-2">{stats.totalCartas.arvore} cartas</span>
					</div>
					<div>
						<span className="text-slate-400">Matriz:</span>
						<span className="text-slate-200 font-semibold ml-2">{stats.totalCartas.matriz} cartas</span>
					</div>
				</div>
			</div>

			{/* Curva de Mana - Apenas Deck da Árvore */}
			<div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
				<h3 className="text-lg font-semibold text-slate-200 mb-4">⚡ Curva de Fortunas (Deck da Árvore)</h3>
				<div className="h-48">
					<ResponsiveContainer width="100%" height="100%">
						<BarChart data={stats.curvaMana}>
							<XAxis
								dataKey="custo"
								axisLine={false}
								tickLine={false}
								tick={{ fill: '#94A3B8', fontSize: 12 }}
							/>
							<YAxis
								axisLine={false}
								tickLine={false}
								tick={{ fill: '#94A3B8', fontSize: 12 }}
							/>
							<Tooltip content={<CustomTooltip />} />
							<Bar
								dataKey="qtd"
								fill="#3B82F6"
								radius={[4, 4, 0, 0]}
								className="hover:fill-blue-400 transition-colors"
							/>
						</BarChart>
					</ResponsiveContainer>
				</div>
			</div>

			{/* Distribuição de Tipos de Mana */}
			{stats.distribuicaoMana.length > 0 && (
				<div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
					<div className="flex justify-between items-center mb-4">
						<h3 className="text-lg font-semibold text-slate-200">🔮 Distribuição de Tipos de Fortunas</h3>

					</div>
					<div className="space-y-3">
						{stats.distribuicaoMana.map((mana, index) => {
							const total = stats.totalCartas.total;
							const percentage = ((mana.qtd / total) * 100).toFixed(1);

							// Buscar o ID da fortuna pelo nome
							const fortunaObj = fortunas?.find(f => f.tipo === mana.nome);

							const fortunaIcon = fortunaObj ? fortunaIcons[fortunaObj.tipo] : null;

							// Determinar se é fortuna quente ou algida
							const isQuente = ['Verbus', 'Ign', 'Karma'].includes(mana.nome);
							const isAlgida = ['Gnos', 'Dnama', 'Omna'].includes(mana.nome);

							return (
								<div key={index} className="flex items-center justify-between p-2 bg-slate-700 rounded">
									<div className="flex items-center space-x-3">

										<img
											src={fortunaIcon}
											alt={mana.nome}
											className="w-6 h-6 object-contain"
											style={{
												filter: isQuente
													? 'drop-shadow(0px 0px 3px #FF5000)'
													: isAlgida
														? 'drop-shadow(0px 0px 3px #00a0ff)'
														: 'none',
												WebkitFilter: isQuente
													? 'drop-shadow(0px 0px 3px #FF5000)'
													: isAlgida
														? 'drop-shadow(0px 0px 3px #00a0ff)'
														: 'none'
											}}
										/>

										<span className="text-slate-300 text-sm font-medium">{mana.nome}</span>
									</div>
									<div className="flex items-center space-x-2">
										<span className="text-slate-400 text-sm">{percentage}%</span>
										<span className="text-slate-200 font-semibold text-sm bg-slate-600 px-2 py-1 rounded">{mana.qtd}</span>
									</div>
								</div>
							);
						})}
					</div>
				</div>
			)}

			{/* Distribuição por Tipos com Abas */}
			{stats.distribuicaoTipos.length > 0 && (
				<div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
					<h3 className="text-lg font-semibold text-slate-200 mb-4">Distribuição por Tipos</h3>

					{/* Sistema de Abas */}
					<div className="flex mb-4 bg-slate-700 rounded-lg p-1">
						<button
							onClick={() => setActiveTab('arvore')}
							className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${activeTab === 'arvore'
								? 'bg-green-600 text-white'
								: 'text-slate-300 hover:text-white hover:bg-slate-600'
								}`}
						>
							Árvore
						</button>
						<button
							onClick={() => setActiveTab('matriz')}
							className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${activeTab === 'matriz'
								? 'bg-purple-600 text-white'
								: 'text-slate-300 hover:text-white hover:bg-slate-600'
								}`}
						>
							Matriz
						</button>
					</div>

					{/* Conteúdo das Abas */}
					{activeTab === 'arvore' && (
						<div className="h-56">
							<ResponsiveContainer width="100%" height="100%">
								<PieChart>
									<Pie
										data={stats.distribuicaoTipos}
										cx="50%"
										cy="50%"
										outerRadius={80}
										fill="#8884d8"
										dataKey="qtd"
										label={({ nome, qtd }) => `${nome}: ${qtd}`}
									>
										{stats.distribuicaoTipos.map((entry, index) => (
											<Cell
												key={`cell-${index}`}
												fill={COLORS.tipos[entry.nome.toLowerCase()] || `hsl(${index * 45}, 70%, 60%)`}
											/>
										))}
									</Pie>
									<Tooltip content={<CustomTooltip />} />
								</PieChart>
							</ResponsiveContainer>
						</div>
					)}

					{activeTab === 'matriz' && (
						<FortuneChart arvore={arvore} matriz={matriz} />
					)}
				</div>
			)}

			{/* Distribuição por Raridades */}
			{stats.distribuicaoRaridades.length > 0 && (
				<div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
					<h3 className="text-lg font-semibold text-slate-200 mb-4">💎 Distribuição por Raridades</h3>
					<div className="space-y-2">
						{stats.distribuicaoRaridades.map((raridade, index) => {
							const total = stats.totalCartas.total;
							const percentage = ((raridade.qtd / total) * 100).toFixed(1);
							const color = COLORS.raridades[raridade.nome.toLowerCase()] || '#6B7280';

							return (
								<div key={index} className="flex items-center justify-between">
									<div className="flex items-center space-x-2">
										<div
											className="w-3 h-3 rounded-full"
											style={{ backgroundColor: color }}
										/>
										<span className="text-slate-300 text-sm">{raridade.nome}</span>
									</div>
									<div className="flex items-center space-x-2">
										<span className="text-slate-400 text-sm">{percentage}%</span>
										<span className="text-slate-200 font-semibold text-sm">{raridade.qtd}</span>
									</div>
								</div>
							);
						})}
					</div>
				</div>
			)}
		</div>
	);
};

export default DeckStats;